import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Mic,
  Upload,
  Play,
  Pause,
  Trash2,
  Download,
  Check,
  X,
  Clock,
  FileAudio,
  AlertCircle,
  Edit,
  Save,
  FileText,
  Volume2,
  Loader2,
  ArrowLeft
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const MIN_DURATION_SECONDS = 1200; // 20 minutes

const DEFAULT_SCRIPT = `Section 1 – Warm Introduction

Hi there! My name is Benison Thomas, and this is my voice sample for cloning purposes.
In this recording, you'll hear me speak naturally, with different tones, emotions, and topics.
Some parts will be storytelling, others conversational, and some will sound like I'm giving instructions or sharing ideas.
Let's get started!

Section 2 – Everyday Conversation

Hey! How's your day going?
Mine's been pretty good — busy, but productive.
I started my morning with a cup of coffee, checked my messages, and jumped straight into work.
You know that feeling when the to-do list looks endless? Yeah, that was me today.
But I like days like this — full of energy and progress.

Sometimes, I pause for a few minutes, step outside, and just breathe.
A few moments of silence can reset everything.
It's funny how a simple pause can make such a big difference.

Section 3 – Storytelling

Let me tell you a story.
A few years ago, I was working late on a project — the kind that just wouldn't end.
It was past midnight. My eyes were tired, my brain was foggy, and I was about to call it a day.
And then, suddenly, I found the missing piece of the puzzle — that one small fix that made everything work.
It felt like magic.
That's when I realized: sometimes breakthroughs happen right when you're about to give up.

Section 4 – Emotional Range

Now, let's switch tones.
Imagine you've just received wonderful news — something you've been waiting for, for a long time.
You feel your heart beating faster, your eyes light up, and you can't stop smiling.
That's pure joy.

Now, on the other side, think about disappointment.
The plan didn't work.
The message you expected never came.
The silence feels heavy.
But here's the truth — that's life.
Ups and downs, highs and lows — each emotion teaches us something new.

Section 5 – Instructional Tone

Let's move to a more informative tone.
Here's a quick step-by-step guide to organize your day efficiently:

Start by writing down your top three priorities for the day.

Block time for focused work — no notifications, no distractions.

Take a five-minute break every hour to refresh your mind.

Review your progress in the evening and plan for tomorrow.

Remember: consistency always beats intensity.

Section 6 – Persuasive/Business Style

Imagine you're running a business.
You have a great product, but you're not sure how to connect with your audience.
The key is simple: tell a story that people care about.
Don't just sell — connect.
People don't buy products; they buy emotions, trust, and transformation.
That's what makes marketing truly powerful.

Now, let's say you're presenting this to investors.
You'd speak with confidence, clarity, and calm authority.
Something like this:
"Our vision is to create technology that simplifies everyday life — seamlessly blending human experience with intelligent automation."

Section 7 – Light Humor

You know what's funny?
No matter how many alarms I set, I always manage to wake up five minutes before the last one rings.
It's like my body knows it's time to give up on sleep.
Or that moment when your phone slips from your hand, and you perform a ninja-style rescue mid-air — yeah, that's one of my secret talents.

Section 8 – Calm, Reflective Mood

Sometimes, I like to slow down and reflect.
We live in a world that moves so fast — notifications, meetings, updates, endless scrolling.
But when we pause and breathe, we realize what truly matters.
Moments with family.
A simple meal.
A genuine smile.
Those are the memories that stay.

Section 9 – Dialogue Sample

Here's a short conversation example:

Person A: Hey, did you finish the project?
Person B: Almost. Just fixing a few final details.
Person A: Great! We're on schedule then.
Person B: Yeah, feels good to be ahead for once.
Person A: Coffee later?
Person B: Always! You read my mind.

Section 10 – Technical Explanation

Now, let's switch to a technical tone.
Voice cloning uses machine learning to analyze the unique features of a person's voice — tone, pitch, pacing, and pronunciation.
Once trained, the model can synthesize speech that sounds nearly identical to the original speaker.
The key to accuracy is variety in the recording — emotion, tone, speed, and vocabulary.
That's exactly what this script provides.

Section 11 – Motivational Tone

Here's something I believe deeply:
You don't need everything figured out before you start.
Just begin.
Progress is messy, imperfect, and unpredictable — but that's what makes it real.
Every expert was once a beginner.
Every success story started with one small, brave step forward.
Keep going, even when it's hard.
Because your persistence will pay off.

Section 12 – Calm and Soothing

Let's slow it down for a moment.
Imagine sitting by the ocean.
You can hear the gentle rhythm of the waves.
The air is cool, the sky is wide, and everything feels peaceful.
Your mind quiets.
You feel grounded, centered, alive.
This is the voice of calm — soft, balanced, and relaxed.

Section 13 – Dynamic Range (Soft → Loud)

Now, let's practice vocal range.
Softly: "whispering words of encouragement…"
Louder: "and speaking with energy and power when it matters!"
Varying volume gives your voice texture and realism.
AI voice models love this — it helps them learn how your tone changes naturally.

Section 14 – News/Neutral Read

And now, a neutral read, like a news presenter:
"Today, global markets showed signs of recovery after weeks of volatility.
Analysts attribute the rebound to positive consumer data and stronger investor confidence.
Meanwhile, in technology, new advances in artificial intelligence continue to transform industries worldwide."

Section 15 – Natural Transitions

Transitions are important too:
"On a different note…"
"Let's take a quick break and come back refreshed."
"Now that we've covered the basics, let's dive deeper."
Such expressions make the voice feel fluid and conversational.

Section 16 – Character Voices (Optional)

Let's add a bit of fun with a character line or two:
In a deep, dramatic voice: "You can run… but you can't hide from innovation."
In a cheerful tone: "Hey there! Ready to start the adventure?"
This adds vocal diversity that AI models can use for expressive synthesis.

Section 17 – Language Sample (Optional Short)

To improve accent flexibility, here are a few neutral sentences in different languages:
Spanish: "Hola, me llamo Benison, y estoy grabando esta muestra de voz para un proyecto de inteligencia artificial."
Hindi: "नमस्ते, मेरा नाम बेनिसन है, और मैं अपनी आवाज़ की रिकॉर्डिंग कर रहा हूँ।"

Section 18 – Closing Words

Before we end this voice sample, let's summarize:
We've covered a wide range of tones — casual, formal, emotional, instructional, humorous, calm, and persuasive.
Each one helps the AI understand how I express myself naturally.

So, to whoever is training this voice model:
Please ensure it represents me authentically — with warmth, clarity, and a touch of personality.

Thank you for listening.
This is Benison Thomas, and this concludes my 20-minute voice recording.`;

export default function VoiceModelSettings() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [voiceFiles, setVoiceFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  
  // Recording state
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [isEditingScript, setIsEditingScript] = useState(false);
  const [recordingScript, setRecordingScript] = useState("");
  
  // Voice cloning state
  const [cloningVoice, setCloningVoice] = useState(false);
  const [voiceName, setVoiceName] = useState("");
  const [showCloneDialog, setShowCloneDialog] = useState(false);

  // Refs
  const mediaRecorderRef = useRef(null);
  const audioStreamRef = useRef(null);
  const recordingIntervalRef = useRef(null);
  const audioChunksRef = useRef([]);

  const totalDuration = voiceFiles.reduce((sum, file) => sum + (file.duration || 0), 0);
  const progressPercentage = Math.min((totalDuration / MIN_DURATION_SECONDS) * 100, 100);

  useEffect(() => {
    fetchVoiceFiles();
    fetchRecordingScript();
    
    return () => {
      // Cleanup
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
      }
      if (audioStreamRef.current) {
        audioStreamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  const fetchVoiceFiles = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/ai/voice-files`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVoiceFiles(response.data);
    } catch (error) {
      console.error("Failed to load voice files:", error);
      toast.error("Failed to load voice files");
    } finally {
      setLoading(false);
    }
  };

  const fetchRecordingScript = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/ai/recording-script`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRecordingScript(response.data.script_content || DEFAULT_SCRIPT);
    } catch (error) {
      console.error("Failed to fetch recording script:", error);
      setRecordingScript(DEFAULT_SCRIPT);
    }
  };

  const saveRecordingScript = async () => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/ai/recording-script`, 
        { script_content: recordingScript },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Recording script saved successfully");
      setIsEditingScript(false);
    } catch (error) {
      console.error("Failed to save recording script:", error);
      toast.error("Failed to save recording script");
    }
  };

  const cloneVoice = async () => {
    if (!voiceName.trim()) {
      toast.error("Please enter a name for your cloned voice");
      return;
    }

    if (voiceFiles.length === 0) {
      toast.error("Please upload at least one voice file before cloning");
      return;
    }

    setCloningVoice(true);
    try {
      const token = localStorage.getItem("token");
      const formData = new FormData();
      formData.append("voice_name", voiceName);

      const response = await axios.post(`${API}/ai/clone-voice`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });

      toast.success(`Voice "${voiceName}" cloned successfully! You can now use it for text-to-speech.`);
      setShowCloneDialog(false);
      setVoiceName("");
    } catch (error) {
      console.error("Failed to clone voice:", error);
      toast.error(error.response?.data?.detail || "Failed to clone voice");
    } finally {
      setCloningVoice(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/x-wav'];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Please upload MP3 or WAV files only");
      return;
    }

    // Validate file size (500MB limit)
    if (file.size > 500 * 1024 * 1024) {
      toast.error("File size should be less than 500MB");
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      const token = localStorage.getItem("token");
      const formData = new FormData();
      formData.append("file", file);

      const response = await axios.post(`${API}/ai/voice-files/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
        }
      });

      toast.success("Voice file uploaded successfully");
      fetchVoiceFiles();
    } catch (error) {
      console.error("Failed to upload file:", error);
      toast.error("Failed to upload file");
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioStreamRef.current = stream;

      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        setAudioBlob(audioBlob);
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingDuration(0);

      // Update duration every second
      recordingIntervalRef.current = setInterval(() => {
        setRecordingDuration((prev) => prev + 1);
      }, 1000);
    } catch (error) {
      console.error("Error starting recording:", error);
      toast.error("Failed to start recording. Please check microphone permissions.");
    }
  };

  const pauseRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.pause();
      setIsPaused(true);
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
      }
    }
  };

  const resumeRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'paused') {
      mediaRecorderRef.current.resume();
      setIsPaused(false);
      recordingIntervalRef.current = setInterval(() => {
        setRecordingDuration((prev) => prev + 1);
      }, 1000);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setIsPaused(false);
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
      }
      if (audioStreamRef.current) {
        audioStreamRef.current.getTracks().forEach(track => track.stop());
      }
    }
  };

  const saveRecording = async () => {
    if (!audioBlob) return;

    try {
      const token = localStorage.getItem("token");
      const formData = new FormData();
      formData.append("audio", audioBlob, `recording_${Date.now()}.webm`);
      formData.append("duration", recordingDuration);

      const response = await axios.post(`${API}/ai/voice-files/record`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });

      toast.success("Recording saved successfully");
      setAudioBlob(null);
      setRecordingDuration(0);
      fetchVoiceFiles();
    } catch (error) {
      console.error("Failed to save recording:", error);
      toast.error("Failed to save recording");
    }
  };

  const deleteVoiceFile = async (fileId) => {
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/ai/voice-files/${fileId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Voice file deleted");
      fetchVoiceFiles();
    } catch (error) {
      console.error("Failed to delete file:", error);
      toast.error("Failed to delete file");
    }
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    else if (bytes < 1048576) return (bytes / 1024).toFixed(2) + ' KB';
    else return (bytes / 1048576).toFixed(2) + ' MB';
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header with Back Button */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/ai/voice-models')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Voice Models
        </Button>
        <div>
          <h1 className="text-2xl font-bold">Voice Model Settings</h1>
          <p className="text-muted-foreground">Configure your voice cloning settings and upload voice samples</p>
        </div>
      </div>

      {/* Progress Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-blue-600" />
            Voice Cloning Progress
          </CardTitle>
          <CardDescription>
            Upload at least 20 minutes of voice samples for optimal cloning quality
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Duration</span>
              <span className="font-medium">
                {formatDuration(totalDuration)} / 20 minutes
              </span>
            </div>
            <Progress value={progressPercentage} className="h-2" />
            {totalDuration >= MIN_DURATION_SECONDS ? (
              <div className="flex items-center gap-2 text-sm text-green-600">
                <Check className="w-4 h-4" />
                <span>Ready for voice cloning!</span>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                {formatDuration(MIN_DURATION_SECONDS - totalDuration)} remaining
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Recording Script Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-emerald-600" />
                Recording Script
              </CardTitle>
              <CardDescription>
                Use this script when recording your voice for best results
              </CardDescription>
            </div>
            <div className="flex gap-2">
              {isEditingScript ? (
                <>
                  <Button onClick={saveRecordingScript} size="sm">
                    <Save className="w-4 h-4 mr-2" />
                    Save
                  </Button>
                  <Button onClick={() => {
                    setIsEditingScript(false);
                    fetchRecordingScript(); // Reset to saved version
                  }} variant="outline" size="sm">
                    Cancel
                  </Button>
                </>
              ) : (
                <Button onClick={() => setIsEditingScript(true)} variant="outline" size="sm">
                  <Edit className="w-4 h-4 mr-2" />
                  Edit Script
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isEditingScript ? (
            <Textarea
              value={recordingScript}
              onChange={(e) => setRecordingScript(e.target.value)}
              className="min-h-[400px] font-mono text-sm"
              placeholder="Enter your recording script here..."
            />
          ) : (
            <div className="bg-slate-50 p-4 rounded-lg border max-h-[400px] overflow-y-auto overflow-x-hidden">
              <pre className="whitespace-pre-wrap break-words font-sans text-sm text-slate-700 leading-relaxed" style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                {recordingScript}
              </pre>
            </div>
          )}
          <p className="text-xs text-muted-foreground mt-3">
            💡 Tip: This script covers multiple tones, emotions, and speaking styles for optimal voice cloning. Read it naturally and expressively when recording.
          </p>
        </CardContent>
      </Card>

      {/* Record Voice Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mic className="w-5 h-5 text-red-600" />
            Record Voice
          </CardTitle>
          <CardDescription>
            Record your voice directly using your microphone
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
            <div className="flex items-center gap-3">
              <Mic className={`w-8 h-8 ${isRecording ? 'text-red-600 animate-pulse' : 'text-gray-400'}`} />
              <div>
                <p className="font-medium">
                  {isRecording ? (isPaused ? 'Paused' : 'Recording...') : 'Ready to record'}
                </p>
                <p className="text-sm text-muted-foreground">
                  Duration: {formatDuration(recordingDuration)}
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              {!isRecording ? (
                <Button onClick={startRecording}>
                  <Mic className="w-4 h-4 mr-2" />
                  Start Recording
                </Button>
              ) : (
                <>
                  {!isPaused ? (
                    <Button onClick={pauseRecording} variant="outline">
                      <Pause className="w-4 h-4 mr-2" />
                      Pause
                    </Button>
                  ) : (
                    <Button onClick={resumeRecording} variant="outline">
                      <Play className="w-4 h-4 mr-2" />
                      Resume
                    </Button>
                  )}
                  <Button onClick={stopRecording} variant="destructive">
                    <X className="w-4 h-4 mr-2" />
                    Stop
                  </Button>
                </>
              )}
            </div>
          </div>

          {audioBlob && (
            <div className="p-4 bg-green-50 rounded-lg border border-green-200 space-y-3">
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-green-600" />
                <span className="text-sm font-medium text-green-800">Recording complete!</span>
              </div>
              <audio controls src={URL.createObjectURL(audioBlob)} className="w-full" />
              <div className="flex gap-2">
                <Button onClick={saveRecording} className="flex-1">
                  <Download className="w-4 h-4 mr-2" />
                  Save Recording
                </Button>
                <Button onClick={() => setAudioBlob(null)} variant="outline" className="flex-1">
                  <X className="w-4 h-4 mr-2" />
                  Discard
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Upload Voice Files Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Upload className="w-5 h-5 text-purple-600" />
            Upload Voice Files
          </CardTitle>
          <CardDescription>
            Upload existing voice recordings (.mp3 or .wav)
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="border-2 border-dashed rounded-lg p-8 text-center">
              <Input
                type="file"
                accept=".mp3,.wav,audio/mpeg,audio/wav"
                onChange={handleFileUpload}
                disabled={uploading}
                className="hidden"
                id="file-upload"
              />
              <Label htmlFor="file-upload" className="cursor-pointer">
                <div className="flex flex-col items-center gap-2">
                  <Upload className="w-12 h-12 text-muted-foreground" />
                  <p className="text-sm font-medium">
                    {uploading ? 'Uploading...' : 'Click to upload or drag and drop'}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    MP3 or WAV files (Max 500MB)
                  </p>
                </div>
              </Label>
            </div>

            {uploading && (
              <div className="space-y-2">
                <Progress value={uploadProgress} />
                <p className="text-sm text-center text-muted-foreground">{uploadProgress}% uploaded</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Voice Files List */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <FileAudio className="w-5 h-5 text-green-600" />
                Uploaded Voice Files
              </CardTitle>
              <CardDescription>
                {voiceFiles.length} file(s) • Total: {formatDuration(totalDuration)}
              </CardDescription>
            </div>
            {voiceFiles.length > 0 && (
              <Button onClick={() => setShowCloneDialog(true)} variant="default">
                <Volume2 className="w-4 h-4 mr-2" />
                Clone Voice
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {voiceFiles.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <FileAudio className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No voice files uploaded yet</p>
              <p className="text-sm">Upload audio files or record your voice to get started</p>
            </div>
          ) : (
            <div className="space-y-2">
              {voiceFiles.map((file) => (
                <div
                  key={file.id}
                  className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                >
                  <div className="flex items-center gap-3 flex-1">
                    <FileAudio className="w-5 h-5 text-green-600" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{file.filename}</p>
                      <div className="flex items-center gap-3 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatDuration(file.duration)}
                        </span>
                        <span>{formatFileSize(file.file_size)}</span>
                        <span>{new Date(file.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => window.open(`${BACKEND_URL}${file.file_path}`, '_blank')}
                    >
                      <Download className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => deleteVoiceFile(file.id)}
                    >
                      <Trash2 className="w-4 h-4 text-red-600" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Clone Voice Dialog */}
      <Dialog open={showCloneDialog} onOpenChange={setShowCloneDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Clone Your Voice</DialogTitle>
            <DialogDescription>
              Create a cloned voice using your uploaded voice files. This will send your files to ElevenLabs to create a personalized voice model.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="voice-name">Voice Name</Label>
              <Input
                id="voice-name"
                value={voiceName}
                onChange={(e) => setVoiceName(e.target.value)}
                placeholder="e.g., My Cloned Voice"
              />
              <p className="text-xs text-muted-foreground">
                Give your cloned voice a memorable name
              </p>
            </div>
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-800">
                📋 {voiceFiles.length} voice file(s) will be used for cloning
              </p>
              <p className="text-xs text-blue-600 mt-1">
                Total duration: {formatDuration(totalDuration)}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCloneDialog(false)} disabled={cloningVoice}>
              Cancel
            </Button>
            <Button onClick={cloneVoice} disabled={cloningVoice || !voiceName.trim()}>
              {cloningVoice ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Cloning...
                </>
              ) : (
                <>
                  <Volume2 className="w-4 h-4 mr-2" />
                  Clone Voice
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
